import React, { useState, useRef } from 'react';
// import { Button } from '@/components/ui/button';
// import { Card } from '@/components/ui/card';
// import { useToast } from '@/hooks/use-toast';
import { Upload, FileText, CheckCircle } from 'lucide-react';
import useExpenses from '../../hooks/useExpenseData';

const Pluto = ({
    onUpload,
    maxSizeBytes = 10 * 1024 * 1024,
    title = 'Upload Expense File',
    description = 'Select your expense CSV file to process',
}) => {
    const [selectedFile, setSelectedFile] = useState(null);
    const [isDragOver, setIsDragOver] = useState(false);
    const [isUploading, setIsUploading] = useState(false);
    const [isRefreshingSuppliers, setIsRefreshingSuppliers] = useState(false);
    const [statusMessage, setStatusMessage] = useState('');
    const [statusType, setStatusType] = useState(''); // success | error
    const fileInputRef = useRef(null);

    const { saveExpenses, refreshSuppliers } = useExpenses();

    const isCsvFile = (file) =>
        file.name.toLowerCase().endsWith('.csv') ||
        file.type === 'text/csv' ||
        file.type === 'application/vnd.ms-excel';

    const validateFile = (file) => {
        if (!isCsvFile(file)) {
            setStatusType('error');
            setStatusMessage('Invalid file type. Please upload a CSV file.');
            return false;
        }

        if (file.size > maxSizeBytes) {
            setStatusType('error');
            setStatusMessage(`File size exceeds ${(maxSizeBytes / 1024 / 1024).toFixed(0)} MB.`);
            return false;
        }
        setStatusMessage('');
        return true;
    };

    const handleFileSelect = (file) => {
        if (!validateFile(file)) return;
        setSelectedFile(file);
        setStatusType('success');
        setStatusMessage(`${file.name} is ready to upload.`);
    };

    const handleDrop = (e) => {
        e.preventDefault();
        setIsDragOver(false);
        const files = Array.from(e.dataTransfer.files);
        if (files.length > 0) {
            handleFileSelect(files[0]);
        }
    };

    const handleDragOver = (e) => {
        e.preventDefault();
        setIsDragOver(true);
    };

    const handleDragLeave = (e) => {
        e.preventDefault();
        setIsDragOver(false);
    };

    const handleFileInputChange = (e) => {
        const files = e.target.files;
        if (files && files.length > 0) {
            handleFileSelect(files[0]);
        }
    };

    const handleSubmit = async () => {
        if (!selectedFile) {

            return;
        }

        setIsUploading(true);
        setStatusMessage('');

        try {
            const result = await saveExpenses(selectedFile);

            if (result?.status) {
                setStatusType('success');
                setStatusMessage(result.message || 'File uploaded successfully.');
                setSelectedFile(null);
                if (fileInputRef.current) fileInputRef.current.value = '';
            } else {
                setStatusType('error');
                setStatusMessage(result?.message || 'Upload failed.');
            }
        } catch (err) {
            console.error(err);
            setStatusType('error');
            setStatusMessage('Unexpected error occurred while uploading.');
        } finally {
            setIsUploading(false);
        }
    };

    const handleRefreshSuppliers = async () => {
        setIsRefreshingSuppliers(true);
        setStatusMessage('');

        try {
            const result = await refreshSuppliers();

            if (result?.status) {
                setStatusType('success');
                setStatusMessage(result.message || 'Suppliers refreshed successfully.');
            } else {
                setStatusType('error');
                setStatusMessage(result?.message || 'Failed to refresh suppliers.');
            }
        } catch (err) {
            console.error(err);
            setStatusType('error');
            setStatusMessage('Unexpected error occurred while refreshing suppliers.');
        } finally {
            setIsRefreshingSuppliers(false);
        }
    };

    const openFileDialog = () => {
        if (fileInputRef.current) {
            fileInputRef.current.click();
        }
    };

    const handleKeyDown = (e) => {
        if (e.key === 'Enter' || e.key === ' ') {
            e.preventDefault();
            openFileDialog();
        }
    };

    const formatSize = (bytes) => {
        if (bytes >= 1024 * 1024) {
            return `${(bytes / (1024 * 1024)).toFixed(2)} MB`;
        }
        return `${(bytes / 1024).toFixed(1)} KB`;
    };

    //console.log("Pluto render", isUploading, selectedFile);

    return (
        <div className="w-full max-w-md mx-auto space-y-4">
            {/* <Card className="p-6"> */}
            <div className="space-y-4">
                <div className="text-center">
                    <h2 className="text-2xl font-bold text-foreground mb-2">{title}</h2>
                    <p className="text-muted-foreground">{description}</p>
                    <a
                        href="https://app.plutocard.io/"
                        target="_blank"
                        rel="noopener noreferrer"
                        className="inline-block mt-2 text-blue-500 hover:text-blue-700 underline"
                    >
                        Open Pluto Card portal
                    </a>
                </div>

                <div
                    role="button"
                    tabIndex={0}
                    className={`
              relative border-2 border-dashed rounded-lg p-8 text-center cursor-pointer transition-all duration-200
              ${isDragOver
                            ? 'border-upload-border bg-upload-zone-hover'
                            : 'border-border bg-upload-zone hover:bg-upload-zone-hover hover:border-upload-border'
                        }
            `}
                    onDrop={handleDrop}
                    onDragOver={handleDragOver}
                    onDragLeave={handleDragLeave}
                    onClick={openFileDialog}
                    onKeyDown={handleKeyDown}
                >
                    <input
                        ref={fileInputRef}
                        type="file"
                        accept=".csv"
                        onChange={handleFileInputChange}
                        className="hidden"
                    />

                    <div className="flex flex-col items-center space-y-3">
                        {selectedFile ? (
                            <>
                                <CheckCircle className="h-12 w-12 text-success" />
                                <div>
                                    <p className="font-medium text-foreground">
                                        {selectedFile.name}
                                    </p>
                                    <p className="text-sm text-muted-foreground">
                                        {formatSize(selectedFile.size)}
                                    </p>
                                </div>
                            </>
                        ) : (
                            <>
                                <Upload className="h-12 w-12 text-muted-foreground" />
                                <div>
                                    <p className="font-medium text-foreground">
                                        Click to upload or drag and drop
                                    </p>
                                    <p className="text-sm text-muted-foreground">
                                        CSV files up to {(maxSizeBytes / (1024 * 1024)).toFixed(0)} MB
                                    </p>
                                </div>
                            </>
                        )}
                    </div>
                </div>

                {selectedFile && (
                    <div className="flex items-center space-x-2 p-3 bg-secondary rounded-lg">
                        <FileText className="h-4 w-4 text-secondary-foreground" />
                        <span className="text-sm font-medium text-secondary-foreground">
                            Ready to upload: {selectedFile.name}
                        </span>
                    </div>
                )}

                {statusMessage && (
                    <div
                        className={`p-3 rounded text-sm font-medium ${statusType === 'success'
                            ? 'bg-green-100 text-green-700'
                            : 'bg-red-100 text-red-700'
                            }`}
                    >
                        {statusMessage}
                    </div>
                )}
                <button onClick={handleSubmit} disabled={!selectedFile || isUploading} className={"bg-blue-500 hover:bg-blue-700 text-white font-bold py-2 px-4 rounded" + ((!selectedFile || isUploading) ? " cursor-not-allowed" : "")} >
                    {isUploading ? 'Uploading...' : 'Submit'}
                </button>
                <button onClick={handleRefreshSuppliers} disabled={isRefreshingSuppliers} className={"bg-green-500 hover:bg-green-700 text-white font-bold py-2 px-4 rounded ml-2" + (isRefreshingSuppliers ? " cursor-not-allowed" : "")} >
                    {isRefreshingSuppliers ? 'Refreshing...' : 'Refresh Suppliers'}
                </button>

            </div>
            {/* </Card> */}
        </div>
    );
};

export default Pluto;
